import { useEffect, useMemo } from 'react';

import { faGrid2 } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { faTeeth, faTooth } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/videa-components/src/index';
import { cn } from '@repo/videa-components/src/lib/utils';
import { Checkbox } from '@repo/videa-components/src/ui/checkbox';
import { useAtomValue } from 'jotai';

import type { TTreatmentResponse } from '@/modules/claims/freeClaims/insuranceExport/types';
import { selectedTreatmentsAtom } from '@/modules/claims/freeClaims/state';
import { capitalize } from '@/utils/string';

type SelectServicesProps = {
  defaultTreatments: TTreatmentResponse[];
  selectedTreatmentsDraft: string[];
  isEditing?: boolean;
  onSetSelectedTreatmentsDraft: (treatments: string[]) => void;
};

const getCodeDisplay = (treatment: TTreatmentResponse) => {
  const hasToothId = !!Number(treatment.toothId);
  const hasQuadrant = !!treatment.quadrant;

  return hasToothId
    ? `${treatment.toothId} - ${treatment.txCode}`
    : hasQuadrant
      ? `${treatment.quadrant} - ${treatment.txCode}`
      : `FM - ${treatment.txCode}`;
};

export const SelectServices = ({
  defaultTreatments,
  selectedTreatmentsDraft,
  isEditing,
  onSetSelectedTreatmentsDraft,
}: SelectServicesProps) => {
  // atoms
  const selectedTreatmentsAtomValue = useAtomValue(selectedTreatmentsAtom);
  const selectedTreatments = useMemo(
    () => selectedTreatmentsAtomValue ?? [],
    [selectedTreatmentsAtomValue],
  );

  // Filter treatments to display based on edit mode:
  // - In edit mode: show all treatments
  // - In view mode: only show selected treatments
  const treatmentsToDisplay = useMemo(() => {
    if (isEditing) {
      return defaultTreatments;
    }
    return defaultTreatments.filter((treatment) =>
      selectedTreatmentsDraft.includes(treatment.txId.toString()),
    );
  }, [defaultTreatments, selectedTreatmentsDraft, isEditing]);

  // hooks
  const { t } = useLingui();

  // Treatment group name mapping with translations
  const txGroupNameMap = {
    'scaling and root planing': t`Scaling and Root Planing`,
    srp: t`Scaling and Root Planing`,
    crown: t`Crown`,
    filling: t`Filling`,
    extraction: t`Extraction`,
    'root canal': t`Root Canal`,
    implant: t`Implant`,
    bridge: t`Bridge`,
    denture: t`Denture`,
    cleaning: t`Cleaning`,
    examination: t`Examination`,
  };

  // handlers
  const handleCheckboxChange = (txId: string) => {
    if (!isEditing) return;

    const newSelectedTreatments = selectedTreatmentsDraft.includes(txId)
      ? selectedTreatmentsDraft.filter((id) => id !== txId)
      : [...selectedTreatmentsDraft, txId];

    // Prevent deselection of the last treatment
    if (newSelectedTreatments.length === 0) {
      return;
    }

    onSetSelectedTreatmentsDraft(newSelectedTreatments);
  };

  // useEffects
  useEffect(() => {
    if (selectedTreatments && selectedTreatments.length > 0) {
      onSetSelectedTreatmentsDraft(selectedTreatments);
    }
  }, [selectedTreatments, onSetSelectedTreatmentsDraft]);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="vai-w-1/2">
            <Trans>CODE</Trans>
          </TableHead>
          <TableHead className="vai-w-1/2">
            <Trans>DESCRIPTION</Trans>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {treatmentsToDisplay.map((treatment) => {
          // Check if this treatment is currently selected
          const isTreatmentSelected = selectedTreatmentsDraft.includes(
            treatment.txId.toString(),
          );

          // Disable checkbox if this is the last selected treatment
          const isTreatmentDisabled =
            isTreatmentSelected &&
            selectedTreatmentsDraft.length === 1 &&
            selectedTreatmentsDraft[0] === treatment.txId.toString();

          const codeDisplay = getCodeDisplay(treatment);

          return (
            <TableRow
              key={treatment.txId}
              data-cy={`treatment-row-${treatment.txId}`}
              className={cn(
                'vai-h-12',
                isEditing && isTreatmentDisabled && 'vai-opacity-50',
              )}
            >
              <TableCell className="vai-w-1/2 vai-py-2">
                <div className="vai-flex vai-items-center vai-gap-2 vai-whitespace-nowrap">
                  {isEditing && (
                    <Checkbox
                      data-cy={`treatment-checkbox-${treatment.txId}`}
                      checked={isTreatmentSelected}
                      disabled={isTreatmentDisabled}
                      onCheckedChange={() =>
                        handleCheckboxChange(treatment.txId.toString())
                      }
                    />
                  )}
                  {/* Display appropriate icon based on treatment type */}
                  <FontAwesomeIcon
                    icon={
                      treatment.quadrant
                        ? faGrid2
                        : treatment.toothId && Number(treatment.toothId) !== 0
                          ? faTooth
                          : faTeeth
                    }
                    className="vai-text-slate-600"
                  />
                  {codeDisplay}
                </div>
              </TableCell>
              <TableCell className="vai-w-1/2 vai-py-2">
                <div
                  title={
                    treatment.txGroupName
                      ? (txGroupNameMap[
                          treatment.txGroupName.toLowerCase() as keyof typeof txGroupNameMap
                        ] ?? capitalize(treatment.txGroupName))
                      : ''
                  }
                >
                  {treatment.txGroupName
                    ? (txGroupNameMap[
                        treatment.txGroupName.toLowerCase() as keyof typeof txGroupNameMap
                      ] ?? capitalize(treatment.txGroupName))
                    : ''}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
